import { Dispatch, useEffect, useRef } from 'react';
import { useLocation } from '@reach/router';
import { AppState, MainContextActionTypes } from '@constants';
import { TFunction } from '@contracts';
import { getFromLocalStorage, setOnLocalStorage } from '@utils/localStorage';
import { logger } from '@utils/logger';
import { verifyCouponShared } from '@utils/sharedCouponVerification';

/**
 * Global hook that auto-applies a refCode coupon from URL or localStorage.
 * Runs at the Context provider level so the discount is visible everywhere:
 * mini cart, checkout pre-login, and checkout post-login.
 */
function useAutoApplyRefCode(state: AppState, dispatch: Dispatch<MainContextActionTypes>) {
  const { cartItems, country, currency, price, couponCode } = state;
  const location = useLocation();
  const isApplyingRef = useRef(false);

  useEffect(() => {
    // Don't apply if coupon is already set or if currently applying
    if (couponCode || isApplyingRef.current) return;

    // Check for refCode in URL or localStorage
    const urlParams = new URLSearchParams(location.search);
    const refCodeParam = urlParams.get('refCode');
    const storedRefCode = getFromLocalStorage('link-referral-code');
    const couponToApply = refCodeParam || storedRefCode;

    if (!couponToApply) return;

    // Store refCode from URL to localStorage if it's new
    if (refCodeParam && refCodeParam !== storedRefCode) {
      setOnLocalStorage('link-referral-code', refCodeParam);
    }

    // Only proceed if cart has items
    if (cartItems.length === 0) return;

    isApplyingRef.current = true;

    const applyCoupon = async () => {
      try {
        const result = await verifyCouponShared({
          coupon: couponToApply,
          cartItems,
          country,
          currency,
          price,
          t: ((key: string) => key) as TFunction
        });

        if (result.success) {
          dispatch({ type: 'setCouponCode', data: couponToApply });
          dispatch({ type: 'setCoupon', data: result.couponObject });
          dispatch({ type: 'setCouponError', data: null });

          if (result.discountAmount) {
            dispatch({ type: 'setDiscountAmount', data: result.discountAmount });
          }

          if (result.freeGift) {
            dispatch({ type: 'cartItemAdd', data: result.freeGift });
          }

          logger.debug('🏷️ Auto-applied refCode coupon:', couponToApply);
        } else if (result.error) {
          logger.debug('🏷️ RefCode coupon verification failed:', result.error);
        }
      } catch (error) {
        logger.error('Failed to auto-apply refCode coupon:', error);
      } finally {
        isApplyingRef.current = false;
      }
    };

    applyCoupon();
  }, [cartItems, country, currency, price, couponCode, location.search, dispatch]);
}

export default useAutoApplyRefCode;
